
import React, { useState, useMemo } from 'react';
import { X, Info, Clock, ChevronDown, ChevronUp, AlertCircle, Loader2 } from 'lucide-react';
import { Patient, Approach, CycleFrequency, Indicator } from '../types';
import { INDICATORS, APPROACH_LIST } from '../constants';
import { maskPhone } from '../utils/phone';

interface NewPatientModalProps {
  onClose: () => void;
  onSave: (patient: Patient) => void;
  userProfiles?: any[];
  activeProfile?: any;
  /** Erro devolvido pela API ao tentar criar o paciente. */
  errorMessage?: string | null;
  isSaving?: boolean;
}

const NewPatientModal: React.FC<NewPatientModalProps> = ({
  onClose,
  onSave,
  userProfiles: userProfilesProp,
  activeProfile: activeProfileProp,
  errorMessage = null,
  isSaving = false,
}) => {
  const userProfiles = useMemo(() => {
    if (Array.isArray(userProfilesProp) && userProfilesProp.length) return userProfilesProp;
    try {
      const saved = localStorage.getItem('allodos-user-profiles');
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    return [
      { id: '1', name: 'Dra. Marina Lopes', role: 'Administrador / Psicóloga', email: 'contato@clinicaevoluir.com', status: 'Ativo' },
      { id: '2', name: 'Ana Paula Silva', role: 'Recepcionista', email: 'recepcao@clinicaevoluir.com', status: 'Ativo' }
    ];
  }, [userProfilesProp]);

  const professionals = useMemo(() => {
    return userProfiles.filter((p: any) => {
      const roleLower = (p.role || '').toLowerCase();
      return roleLower.includes('administrador') || roleLower.includes('profissional') || roleLower.includes('psicólog');
    });
  }, [userProfiles]);

  const { activeProfile, isProfessional } = useMemo(() => {
    if (activeProfileProp) {
      const roleLower = (activeProfileProp?.role || '').toLowerCase();
      const isProf = (roleLower.includes('profissional') || roleLower.includes('psicólog') || roleLower.includes('terapeut')) && !roleLower.includes('administrador');
      return { activeProfile: activeProfileProp, isProfessional: isProf };
    }
    try {
      const saved = localStorage.getItem('allodos-user-profiles');
      const activeId = localStorage.getItem('allodos-active-profile-id') || '1';
      if (saved) {
        const list = JSON.parse(saved);
        const profile = activeProfileProp || list.find((p: any) => p.id === activeId) || list[0];
        const roleLower = (profile?.role || '').toLowerCase();
        const isProf = (roleLower.includes('profissional') || roleLower.includes('psicólog') || roleLower.includes('terapeut')) && !roleLower.includes('administrador');
        return { activeProfile: profile, isProfessional: isProf };
      }
    } catch (e) {}
    return { 
      activeProfile: { id: '1', name: 'Dra. Marina Lopes', role: 'Administrador', email: 'contato@clinicaevoluir.com', status: 'Ativo' }, 
      isProfessional: false 
    };
  }, [activeProfileProp]);

  // Bloco 34: a recepção só edita os Dados Cadastrais.
  const isReceptionist = useMemo(() => {
    const roleLower = String((activeProfile as any)?.role || '').toLowerCase();
    return roleLower.includes('recep') || roleLower.includes('secretar');
  }, [activeProfile]);

  const [formData, setFormData] = useState({
    name: '',
    age: '',
    phone: '',
    dob: '',
    email: '',
    emergencyContact: '',
    emergencyPhone: '',
    address: '',
    cep: '',
    cpf: '',
    fatherName: '',
    motherName: '',
    assignedProfessionalId: isProfessional ? activeProfile.id : (professionals[0]?.id || '1'),
    assignedProfessionalName: isProfessional ? activeProfile.name : (professionals[0]?.name || 'Dra. Marina Lopes'),
    mainComplaint: '',
    medications: [] as { name: string; quantity: string; time: string }[],
    approach: Approach.TCC,
    cycleFrequency: CycleFrequency.TRIMESTRAL
  });
  const [selectedIndicators, setSelectedIndicators] = useState<string[]>([]);
  const [hoveredIndicator, setHoveredIndicator] = useState<string | null>(null);

  // Agrupamento de indicadores por domínio
  const groupedIndicators = useMemo(() => {
    return INDICATORS.reduce((acc, ind) => {
      if (!acc[ind.dominio]) acc[ind.dominio] = [];
      acc[ind.dominio].push(ind);
      return acc;
    }, {} as Record<string, Indicator[]>);
  }, []);

  const calculateAge = (dob: string) => {
    if (!dob) return '';
    const birthDate = new Date(dob);
    if (isNaN(birthDate.getTime())) return '';
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age.toString();
  };

  const handleMedicationChange = (index: number, field: 'name' | 'quantity' | 'time', value: string) => {
    const newMedications = [...formData.medications];
    newMedications[index] = { ...newMedications[index], [field]: value };
    setFormData(prev => ({ ...prev, medications: newMedications }));
  };

  const addMedication = () => {
    setFormData(prev => ({
      ...prev,
      medications: [...prev.medications, { name: '', quantity: '', time: '' }]
    }));
  };

  const removeMedication = (index: number) => {
    setFormData(prev => ({
      ...prev,
      medications: prev.medications.filter((_, i) => i !== index)
    }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name === 'dob') {
      const calculatedAge = calculateAge(value);
      setFormData(prev => ({ 
        ...prev, 
        [name]: value,
        age: calculatedAge 
      }));
    } else if (name === 'phone' || name === 'emergencyPhone') {
      // Blocos #47/#48: máscara padrão "(11) 99999-9999", sem perder dígitos.
      setFormData(prev => ({ ...prev, [name]: maskPhone(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleCepChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const cleanCep = rawValue.replace(/\D/g, '').slice(0, 8);
    
    let formattedCep = cleanCep;
    if (cleanCep.length > 5) {
      formattedCep = `${cleanCep.slice(0, 5)}-${cleanCep.slice(5)}`;
    }
    
    setFormData(prev => ({ ...prev, cep: formattedCep }));

    if (cleanCep.length === 8) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
        const data = await response.json();
        if (!data.erro) {
          const addressString = `${data.logradouro}${data.bairro ? `, ${data.bairro}` : ''}, ${data.localidade} - ${data.uf}`;
          setFormData(prev => ({
            ...prev,
            address: addressString
          }));
        }
      } catch (error) {
        console.error('Erro ao buscar o CEP:', error);
      }
    }
  };

  const toggleIndicator = (id: string) => {
    setSelectedIndicators(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  /** Motivo pelo qual o formulário ainda não pode ser enviado (null = pode). */
  const validationMessage = !formData.name.trim()
    ? 'Informe o nome do paciente para continuar.'
    : selectedIndicators.length === 0
      ? 'Selecione pelo menos um indicador clínico.'
      : null;

  const handleSave = () => {
    if (validationMessage) return;

    const newPatient: Patient = {
      id: Date.now().toString(),
      name: formData.name,
      age: formData.age,
      phone: formData.phone,
      dob: formData.dob,
      email: formData.email,
      emergencyContact: formData.emergencyContact,
      emergencyPhone: formData.emergencyPhone,
      address: formData.address,
      cep: formData.cep,
      cpf: formData.cpf,
      fatherName: formData.fatherName,
      motherName: formData.motherName,
      assignedProfessionalId: formData.assignedProfessionalId,
      assignedProfessionalName: formData.assignedProfessionalName,
      mainComplaint: formData.mainComplaint,
      medications: formData.medications,
      activeApproach: formData.approach,
      cycleFrequency: formData.cycleFrequency,
      selectedIndicatorIds: selectedIndicators,
      createdAt: new Date().toLocaleDateString('pt-BR'),
      cycles: []
    };

    onSave(newPatient);
  };

  const inputClasses = "w-full p-3.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:outline-none focus:border-indigo-400 transition-all text-sm shadow-sm dark:text-slate-100";
  const labelClasses = "text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1";

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl animate-in zoom-in-95 border dark:border-slate-800 transition-colors">
        <div className="p-5 sm:p-8 border-b dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/50">
          <div>
            <h3 className="text-2xl font-black text-slate-800 dark:text-slate-100">Novo Paciente</h3>
            <p className="text-xs text-slate-400 dark:text-slate-500 font-bold uppercase tracking-widest mt-1">Configure o perfil inicial do paciente</p>
          </div>
          <button onClick={onClose} className="p-3 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-2xl transition-colors text-slate-500 dark:text-slate-400"><X size={24} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-8 grid grid-cols-1 lg:grid-cols-5 gap-6 sm:gap-12">
          <div className="lg:col-span-2 space-y-8">
            <h4 className="text-[10px] font-black text-indigo-500 dark:text-indigo-400 uppercase tracking-[0.2em] border-b border-indigo-100 dark:border-indigo-900/40 pb-2">Dados Cadastrais</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="col-span-full space-y-1.5">
                <label className={labelClasses}>NOME COMPLETO</label>
                <input name="name" value={formData.name} onChange={handleInputChange} className={inputClasses} placeholder="Ex: João da Silva" />
              </div>
              
              <div className="space-y-1.5">
                <label className={labelClasses}>DATA DE NASCIMENTO</label>
                <input name="dob" type="date" value={formData.dob} onChange={handleInputChange} className={inputClasses} />
              </div>
              <div className="space-y-1.5">
                <label className={labelClasses}>IDADE CALCULADA</label>
                <input name="age" value={formData.age} onChange={handleInputChange} className={`${inputClasses} bg-slate-50 dark:bg-slate-900 font-bold`} placeholder="Calculada automaticamente" readOnly />
              </div>

              {/* Novos Campos Cadastrais */}
              <div className="space-y-1.5">
                <label className={labelClasses}>TELEFONE</label>
                <input type="tel" inputMode="tel" name="phone" value={formData.phone} onChange={handleInputChange} className={inputClasses} placeholder="Ex: (11) 99999-9999" />
              </div>
              <div className="space-y-1.5">
                <label className={labelClasses}>E-MAIL</label>
                <input name="email" type="email" value={formData.email} onChange={handleInputChange} className={inputClasses} placeholder="Ex: joao@gmail.com" />
              </div>

              <div className="space-y-1.5">
                <label className={labelClasses}>CPF</label>
                <input name="cpf" value={formData.cpf} onChange={handleInputChange} className={inputClasses} placeholder="Ex: 000.000.000-00" />
              </div>
              <div className="space-y-1.5">
                <label className={labelClasses}>FONE DE EMERGÊNCIA / RESPONSÁVEL</label>
                <input type="tel" inputMode="tel" name="emergencyPhone" value={formData.emergencyPhone} onChange={handleInputChange} className={inputClasses} placeholder="Ex: (11) 98888-8888" />
              </div>

              <div className="col-span-full grid grid-cols-1 sm:grid-cols-4 gap-5">
                <div className="sm:col-span-1 space-y-1.5">
                  <label className={labelClasses}>CEP</label>
                  <input 
                    name="cep" 
                    value={formData.cep} 
                    onChange={handleCepChange} 
                    className={inputClasses} 
                    placeholder="Ex: 01310-100" 
                    maxLength={9}
                  />
                </div>
                <div className="sm:col-span-3 space-y-1.5">
                  <label className={labelClasses}>ENDEREÇO COMPLETO</label>
                  <input 
                    name="address" 
                    value={formData.address} 
                    onChange={handleInputChange} 
                    className={inputClasses} 
                    placeholder="Ex: Rua das Flores, 123 - Centro, São Paulo - SP" 
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className={labelClasses}>NOME DO PAI</label>
                <input name="fatherName" value={formData.fatherName} onChange={handleInputChange} className={inputClasses} placeholder="Ex: Roberto da Silva" />
              </div>
              <div className="space-y-1.5">
                <label className={labelClasses}>NOME DA MÃE</label>
                <input name="motherName" value={formData.motherName} onChange={handleInputChange} className={inputClasses} placeholder="Ex: Maria da Silva" />
              </div>
            </div>
            
            <div className="bg-slate-50 dark:bg-slate-800/30 p-5 rounded-2xl border border-slate-200 dark:border-slate-800">
               <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-2">Resumo da Seleção</p>
               <div className="flex flex-wrap gap-2">
                 {selectedIndicators.length > 0 ? (
                   <span className="text-xs font-black text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 px-3 py-1 rounded-full border border-indigo-100 dark:border-indigo-800 animate-in fade-in">
                     {selectedIndicators.length} INDICADORES ATIVOS
                   </span>
                 ) : (
                   <span className="text-xs font-bold text-slate-400 dark:text-slate-600 italic">Nenhum indicador selecionado</span>
                 )}
               </div>
            </div>
          </div>

          {/* Bloco 34: a recepção vê, mas não altera, os dados clínicos. */}
          <fieldset disabled={isReceptionist} className="lg:col-span-3 space-y-8 border-0 p-0 m-0 min-w-0 disabled:opacity-80 disabled:cursor-not-allowed">
            <h4 className="text-[10px] font-black text-indigo-500 dark:text-indigo-400 uppercase tracking-[0.2em] border-b border-indigo-100 dark:border-indigo-900/40 pb-2 flex items-center justify-between gap-3">
              <span>Domínios de Acompanhamento Clínico</span>
              {isReceptionist && (
                <span className="text-[9px] font-black uppercase tracking-widest text-slate-400 bg-slate-100 dark:bg-slate-800 px-3 py-1 rounded-lg normal-case">
                  Somente leitura · dados clínicos são do profissional/administrador
                </span>
              )}
            </h4>
            
            <div className="space-y-6 bg-slate-50 dark:bg-slate-800/30 p-5 rounded-2xl border border-slate-200/50 dark:border-slate-800/50">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="space-y-1.5">
                  <label className={labelClasses}>ABORDAGEM TERAPÊUTICA</label>
                  <div className="relative">
                    <select name="approach" value={formData.approach} onChange={handleInputChange} className={`${inputClasses} h-[46px] py-0 font-bold text-slate-700 dark:text-slate-300`}>
                      {APPROACH_LIST.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className={labelClasses}>PERIODICIDADE DO CICLO</label>
                  <div className="relative">
                    <select name="cycleFrequency" value={formData.cycleFrequency} onChange={handleInputChange} className={`${inputClasses} h-[46px] py-0 font-bold text-indigo-700 dark:text-indigo-400 pl-10 appearance-none`}>
                      {Object.values(CycleFrequency).map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 text-indigo-400" size={16} />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className={labelClasses}>PROFISSIONAL RESPONSÁVEL</label>
                  <div className="relative">
                    {!isProfessional ? (
                      <select 
                        name="assignedProfessionalId" 
                        value={formData.assignedProfessionalId} 
                        onChange={(e) => {
                          const selectedId = e.target.value;
                          const prof = professionals.find(p => p.id === selectedId);
                          setFormData(prev => ({
                            ...prev,
                            assignedProfessionalId: selectedId,
                            assignedProfessionalName: prof ? prof.name : ''
                          }));
                        }} 
                        className={`${inputClasses} h-[46px] py-0 font-bold text-indigo-700 dark:text-indigo-400`}
                      >
                        {professionals.map(p => (
                          <option key={p.id} value={p.id}>{p.name} ({p.role})</option>
                        ))}
                      </select>
                    ) : (
                      <div className={`${inputClasses} h-[46px] flex items-center bg-slate-100 dark:bg-slate-800/85 text-slate-500 font-bold text-xs px-4 rounded-xl`}>
                        {activeProfile.name}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="border-t border-slate-200/50 dark:border-slate-700/30"></div>

              <div className="space-y-1.5">
                <label className={labelClasses}>QUEIXA PRINCIPAL</label>
                <textarea name="mainComplaint" value={formData.mainComplaint} onChange={handleInputChange} className={`${inputClasses} min-h-[100px] resize-none leading-relaxed bg-white dark:bg-slate-900`} placeholder="Descreva brevemente o motivo principal da busca por terapia..." />
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <label className={labelClasses}>MEDICAMENTOS EM USO</label>
                  <button 
                    type="button"
                    onClick={addMedication}
                    className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest hover:text-indigo-700 transition-colors cursor-pointer"
                  >
                    + Adicionar Medicamento
                  </button>
                </div>
                
                {formData.medications.length > 0 ? (
                  <div className="overflow-hidden border border-slate-200 dark:border-slate-800 rounded-2xl bg-white dark:bg-slate-900">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-slate-50 dark:bg-slate-800/50 border-b dark:border-slate-800">
                          <th className="p-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Medicamento</th>
                          <th className="p-3 text-[10px] font-black text-slate-400 uppercase tracking-widest w-24">Qtd</th>
                          <th className="p-3 text-[10px] font-black text-slate-400 uppercase tracking-widest w-32">Horário</th>
                          <th className="p-3 w-10"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y dark:divide-slate-800">
                        {formData.medications.map((med, index) => (
                          <tr key={index} className="bg-white dark:bg-slate-900">
                            <td className="p-2">
                              <input 
                                value={med.name}
                                onChange={(e) => handleMedicationChange(index, 'name', e.target.value)}
                                className="w-full p-2 bg-transparent focus:outline-none text-sm dark:text-slate-200 font-medium"
                                placeholder="Nome..."
                              />
                            </td>
                            <td className="p-2 border-l dark:border-slate-800">
                              <input 
                                value={med.quantity}
                                onChange={(e) => handleMedicationChange(index, 'quantity', e.target.value)}
                                className="w-full p-2 bg-transparent focus:outline-none text-sm dark:text-slate-200 font-medium"
                                placeholder="Ex: 1cp"
                              />
                            </td>
                            <td className="p-2 border-l dark:border-slate-800">
                              <input 
                                value={med.time}
                                onChange={(e) => handleMedicationChange(index, 'time', e.target.value)}
                                className="w-full p-2 bg-transparent focus:outline-none text-sm dark:text-slate-200 font-medium"
                                placeholder="Ex: 08:00"
                              />
                            </td>
                            <td className="p-2 text-center">
                              <button 
                                type="button"
                                onClick={() => removeMedication(index)}
                                className="text-rose-400 hover:text-rose-600 transition-colors cursor-pointer"
                              >
                                <X size={14} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-4 border border-dashed border-slate-200 dark:border-slate-800 rounded-2xl text-center bg-white dark:bg-slate-900">
                    <p className="text-xs text-slate-400 font-bold uppercase tracking-widest">Nenhum medicamento registrado</p>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-6">
              {/* Fix: Explicitly cast Object.entries to resolve 'unknown' type error for 'items' */}
              {(Object.entries(groupedIndicators) as [string, Indicator[]][]).map(([domain, items]) => (
                <div key={domain} className="space-y-3">
                  <h5 className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.3em] flex items-center gap-2">
                    <div className="w-1 h-3 bg-indigo-500 rounded-full"></div>
                    {domain}
                  </h5>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {items.map(ind => (
                      <div 
                        key={ind.id} 
                        className="relative"
                        onMouseEnter={() => setHoveredIndicator(ind.id)}
                        onMouseLeave={() => setHoveredIndicator(null)}
                      >
                        <button
                          onClick={() => toggleIndicator(ind.id)}
                          className={`w-full p-3.5 rounded-2xl border text-left transition-all relative group flex items-center justify-between ${
                            selectedIndicators.includes(ind.id) 
                              ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-900/20 shadow-sm' 
                              : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-800 hover:border-indigo-200'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-2 h-2 rounded-full ${ind.eixo === 'positive' ? 'bg-emerald-400' : 'bg-rose-400'}`}></div>
                            <span className={`text-[11px] font-black uppercase tracking-widest ${selectedIndicators.includes(ind.id) ? 'text-indigo-700 dark:text-indigo-400' : 'text-slate-600 dark:text-slate-400'}`}>
                              {ind.nome}
                            </span>
                          </div>
                          <Info size={12} className={`${selectedIndicators.includes(ind.id) ? 'text-indigo-400' : 'text-slate-300 dark:text-slate-600'}`} />
                        </button>

                        {hoveredIndicator === ind.id && (
                          <div className="absolute z-20 left-0 right-0 -top-2 -translate-y-full bg-slate-900 text-white p-3 rounded-xl text-[10px] shadow-xl animate-in fade-in zoom-in-95 pointer-events-none border border-slate-700">
                            <div className="font-black mb-1 text-indigo-300 dark:text-indigo-400 uppercase tracking-widest">{ind.nome}</div>
                            <p className="leading-relaxed opacity-90">{ind.descricao_clinica}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </fieldset>
        </div>

        <div className="p-5 sm:p-8 border-t dark:border-slate-800 border-slate-100 bg-slate-50/80 dark:bg-slate-800/50 space-y-4">
          {/* Diz por que o botão não salva, em vez de simplesmente não reagir. */}
          {(validationMessage || errorMessage) && (
            <div className="flex items-start gap-3 p-4 rounded-2xl bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900/40">
              <AlertCircle size={16} className="text-rose-500 shrink-0 mt-0.5" />
              <p className="text-xs font-bold text-rose-600 dark:text-rose-300 whitespace-pre-line">
                {errorMessage || validationMessage}
              </p>
            </div>
          )}
          <div className="flex gap-4">
            <button onClick={onClose} disabled={isSaving} className="flex-1 p-4 border border-rose-200 dark:border-rose-900/30 bg-rose-50 dark:bg-rose-950/20 rounded-2xl font-black text-rose-500 dark:text-rose-400 hover:bg-rose-100 dark:hover:bg-rose-900/40 transition-all shadow-sm uppercase text-xs tracking-widest leading-none disabled:opacity-50">Cancelar</button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex-1 p-4 bg-emerald-600 text-white rounded-2xl font-black hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-100 dark:shadow-none disabled:opacity-50 disabled:cursor-not-allowed uppercase text-xs tracking-widest leading-none flex items-center justify-center gap-2"
            >
              {isSaving ? <><Loader2 size={14} className="animate-spin" /> Criando...</> : 'Criar Paciente'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewPatientModal;
